import easterConfig from '@/v2/composables/easter_dates.json';
import { SEASONAL_CONFIG } from '@/v2/composables/seasonalConfig.js';
import { useSeasonalAnimationStore } from '@/stores/seasonalAnimation.js';
import { getCountry } from '@/router/queryParam.js';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

export function useSeasonalTheme() {
  const animationStore = useSeasonalAnimationStore();
  const route = useRoute();
  const today = new Date();
  const activeSeasonalTheme =
    Object.entries(SEASONAL_CONFIG).find(([themeKey, theme]) =>
      isSeasonalThemeActive(themeKey, theme, today, route)
    )?.[1] || null;

  const getSeasonalAsset = key => {
    return activeSeasonalTheme?.assets?.[key] ?? null;
  };

  const getSeasonalClass = key => {
    return activeSeasonalTheme?.classes?.[key] ?? [];
  };

  const getSeasonalStyle = key => {
    return activeSeasonalTheme?.styles?.[key] ?? {};
  };

  const triggerSeasonalAnimation = () => {
    if (!activeSeasonalTheme?.animation) return false;

    return animationStore.trigger(activeSeasonalTheme?.animation.durationMs);
  };

  const getAnimationKey = () => {
    return activeSeasonalTheme?.animation?.key ?? null;
  };

  const isAnimationRunning = computed(() => animationStore.isRunning);
  const isAnimationShowing = computed(() => animationStore.isShowing);

  return {
    activeSeasonalTheme,
    getSeasonalAsset,
    getSeasonalClass,
    getSeasonalStyle,
    triggerSeasonalAnimation,
    getAnimationKey,
    isAnimationRunning,
    isAnimationShowing
  };
}

function isSeasonalThemeActive(themeKey, theme, date, route) {
  if (themeKey === 'easter') {
    return isEasterDateInRange(date, route);
  }

  return isDateInRange(date, theme.start, theme.end);
}

function isEasterDateInRange(date, route) {
  const country = getCountry(route);
  const countryCode = getCountryCode(country);
  if (countryCode == null) return false;
  const easterDateRange = getEasterDateRange(date.getFullYear(), countryCode);

  if (!easterDateRange) {
    return false;
  }

  return isDateInRange(date, easterDateRange.start, easterDateRange.end);
}

function getEasterDateRange(year, countryCode) {
  const easterDateRanges = easterConfig.easter_date_ranges?.[String(year)];

  if (!easterDateRanges) {
    return null;
  }

  const usesOrthodoxEaster =
    !!countryCode &&
    easterConfig.orthodox_majority_countries.includes(countryCode);

  return usesOrthodoxEaster
    ? easterDateRanges.orthodox
    : easterDateRanges.western;
}

function getCountryCode(country) {
  if (!country) {
    return null;
  }

  if (typeof country === 'string') {
    return country.toLowerCase();
  }

  return null;
}

function isDateInRange(date, start, end) {
  const year = date.getFullYear();

  const startDate = new Date(year, start.month - 1, start.day);
  const endDate = new Date(year, end.month - 1, end.day + 1);
  // Handles ranges that wrap around New Year (e.g.: Dec 20 - Jan 7)
  if (endDate < startDate) {
    if (date < endDate) {
      // we passed to New Year
      startDate.setFullYear(year - 1);
    } else {
      // before New Year
      endDate.setFullYear(year + 1);
    }
  }

  return date >= startDate && date < endDate;
}
